import React, { useCallback, useEffect, useRef, useState } from "react";
import { Autocomplete, Loader } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { getAgreementPartyNames } from "../utilities/apis/agreements";
import { apiWrapWithErrorWithData } from "../utilities/apiHelpers";

export const PARTY_NAME_LIST_TYPES = {
  AGREEMENTS: "agreementsSearchList",
  DOC_LOCKER: "agreementsDocLockerList",
  SIGNED: "agreementsSignedList",
};

const PartyNameFilter = ({
  value = "",
  onChange,
  listType,
  placeholder = "Party name",
  style,
  ...rest
}) => {
  const [partyNameOptions, setPartyNameOptions] = useState([]);
  const [partyNamesLoading, setPartyNamesLoading] = useState(false);
  const [debouncedSearch] = useDebouncedValue(value, 300);
  const prevDebouncedSearch = useRef(debouncedSearch);

  const fetchPartyNameOptions = useCallback(
    async (search = "") => {
      setPartyNamesLoading(true);
      try {
        const response = await apiWrapWithErrorWithData(
          getAgreementPartyNames({ search, listType }),
        );
        if (response?.success) {
          setPartyNameOptions(response.partyNames || []);
        }
      } finally {
        setPartyNamesLoading(false);
      }
    },
    [listType],
  );

  useEffect(() => {
    const prev = prevDebouncedSearch.current;
    prevDebouncedSearch.current = debouncedSearch;

    if (!debouncedSearch.trim() && !prev.trim()) return;

    fetchPartyNameOptions(debouncedSearch);
  }, [debouncedSearch, fetchPartyNameOptions]);

  const handleChange = (val) => {
    const nextValue = val ?? "";
    onChange?.(nextValue);
    setPartyNamesLoading(true);
    if (!nextValue.trim()) {
      setPartyNameOptions([]);
    }
  };

  const handleDropdownOpen = () => {
    if (!value.trim() && !partyNamesLoading) {
      fetchPartyNameOptions("");
    }
  };

  return (
    <Autocomplete
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      onDropdownOpen={handleDropdownOpen}
      data={partyNamesLoading ? [] : partyNameOptions}
      searchable
      clearable
      withinPortal
      dropdownPosition="bottom"
      maxDropdownHeight={250}
      dropdownComponent="div"
      style={style}
      rightSection={
        partyNamesLoading && !value.trim() ? (
          <Loader size="xs" color="#46BDE1" />
        ) : null
      }
      nothingFound={
        partyNamesLoading ? (
          <div className="flex justify-center py-2">
            <Loader size="sm" color="#46BDE1" />
          </div>
        ) : (
          "No party found"
        )
      }
      {...rest}
    />
  );
};

export default PartyNameFilter;
